// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file SideNav.tsx
 * @input Uses React, HTMLAttributes, ReactNode, StyleX
 * @output Exports SideNav component and SideNavProps
 * @position Core implementation; consumed by index.ts, tested by SideNav.test.tsx
 *
 * Sidebar navigation container with five zones: header + topContent (sticky together),
 * children (scrollable), footer, and footerIcons (sticky bottom).
 *
 * Supports optional resize via drag handle at the inline-end edge.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/SideNav/SideNav.doc.mjs
 * - /packages/core/src/SideNav/SideNav.test.tsx
 * - /packages/core/src/SideNav/index.ts
 * - /apps/storybook/stories/SideNav.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/SideNav/ (showcase blocks)
 */
import { useCallback, useImperativeHandle, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { spacingVars } from "../theme/tokens.stylex.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { SideNavCollapseContext } from "./SideNavCollapseContext.js";
import { SideNavCollapseButton } from "./SideNavCollapseButton.js";
import { useSideNavRenderMode } from "./SideNavRenderContext.js";
import { MobileNav } from "../MobileNav/MobileNav.js";
import { useResizable } from "../Resizable/useResizable.js";
import { ResizeHandle } from "../Resizable/ResizeHandle.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Constants
// =============================================================================

/** Width below which dragging collapses the sidebar (when collapsible). */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const COLLAPSE_THRESHOLD = 160;

// =============================================================================
// Styles
// =============================================================================

const styles = {
  root: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kZKoxP: "x5yr21d",
    kzqmXN: "x1hfn5x7",
    kWkggS: "xgcd1z6",
    kB7OPa: "x9f619",
    kVQacm: "xb3r6kr",
    $$css: true
  },
  rootCollapsed: {
    kzqmXN: "xhgvbfk",
    $$css: true
  },
  topbar: {
    k1xSpc: "x78zum5",
    kXwgrk: "x1q0g3np",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kZKoxP: "xsdox4t",
    kzqmXN: "xh8yej3",
    kWkggS: "xgcd1z6",
    kB7OPa: "x9f619",
    kVQacm: "xb3r6kr",
    $$css: true
  }
};

// =============================================================================
// Types
// =============================================================================

// =============================================================================
// Component
// =============================================================================

/**
 * Sidebar navigation container for application pages.
 *
 * Five vertical zones: sticky header + action area at top,
 * scrollable nav content in the middle, and sticky footer + icon bar at bottom.
 *
 * @example
 * ```
 * <SideNav
 *   header={<SideNavHeading heading="My App" headingHref="/" />}
 *   topContent={<Button label="Create new" variant="primary" />}>
 *   <SideNavSection heading="Main">
 *     <SideNavItem label="Dashboard" isSelected href="/dashboard" />
 *     <SideNavItem label="Projects" href="/projects" />
 *   </SideNavSection>
 * </SideNav>
 * ```
 */
export function SideNav({
  header,
  topContent,
  children,
  footer,
  footerIcons,
  collapsible = false,
  resizable = false,
  xstyle,
  className,
  style,
  'data-testid': testId,
  ref,
  handleRef,
  ...props
}) {
  const t = useTranslator();
  // Parse collapsible prop
  const collapsibleConfig = typeof collapsible === 'object' ? collapsible : {};
  const isCollapsible = !!collapsible;
  const hasCollapseButton = collapsibleConfig.hasButton ?? true;
  const defaultIsCollapsed = collapsibleConfig.defaultIsCollapsed ?? false;
  const controlledCollapsed = collapsibleConfig.isCollapsed;
  const onCollapsedChange = collapsibleConfig.onCollapsedChange;

  // Resizable config
  const resizableConfig = typeof resizable === 'object' ? resizable : {};
  const isResizable = !!resizable;

  // Collapse state (controlled + uncontrolled)
  const isControlled = controlledCollapsed !== undefined;
  const [uncontrolledCollapsed, setUncontrolledCollapsed] = useState(defaultIsCollapsed);
  const collapsed = isControlled ? controlledCollapsed : uncontrolledCollapsed;
  const navRef = useRef(null);
  const collapseStateRef = useRef({
    isCollapsed: collapsed,
    toggle: () => {},
    isCollapsible
  });
  const setCollapsedState = useCallback(value => {
    if (!isControlled) {
      setUncontrolledCollapsed(value);
    }
    onCollapsedChange?.(value);
  }, [isControlled, onCollapsedChange]);

  // Resize hook — callbacks keep SideNav in sync without effects.
  const resizableHook = useResizable({
    defaultSize: resizableConfig.defaultWidth ?? 260,
    minSizePx: resizableConfig.minWidth ?? 180,
    maxSizePx: resizableConfig.maxWidth ?? 480,
    collapsible: isCollapsible,
    collapsedSize: COLLAPSE_THRESHOLD,
    autoSaveId: resizableConfig.autoSaveId,
    onSizeChange: resizableConfig.onWidthChange,
    onCollapseChange: isCollapsible ? setCollapsedState : undefined
  });
  const toggle = useCallback(() => {
    const next = !collapsed;
    collapseStateRef.current = {
      ...collapseStateRef.current,
      isCollapsed: next
    };
    setCollapsedState(next);
    if (isResizable) {
      if (next) {
        resizableHook.collapse();
      } else {
        resizableHook.expand();
      }
    }
  }, [collapsed, setCollapsedState, isResizable, resizableHook]);
  const showResizeHandle = isResizable && !collapsed;
  collapseStateRef.current = {
    isCollapsed: collapsed,
    toggle,
    isCollapsible
  };
  const collapseContext = {
    isCollapsed: collapsed,
    toggle,
    isCollapsible
  };
  useImperativeHandle(handleRef, () => ({
    getCollapseState: () => collapseStateRef.current
  }), []);

  // Render mode — when inside AppShell mobile layout, render subsets
  const renderMode = useSideNavRenderMode();

  // =========================================================================
  // Topbar mode — heading + footerIcons in a horizontal bar
  // =========================================================================
  if (renderMode === 'topbar') {
    return /*#__PURE__*/_jsxs("div", {
      "data-testid": testId,
      ...mergeProps(themeProps('side-nav', {
        mode: 'topbar'
      }), stylex.props(styles.topbar, xstyle), className, style),
      children: [header, /*#__PURE__*/_jsx("div", {
        ...{
          className: "x78zum5 x6s0dn4 xzye2dw xvc5jky"
        },
        children: footerIcons
      })]
    });
  }

  // =========================================================================
  // Drawer mode — render inside MobileNav with heading as header
  // =========================================================================
  const hasDrawerFooter = !!(footer || footerIcons);
  if (renderMode === 'drawer') {
    return /*#__PURE__*/_jsxs(MobileNav, {
      header: header,
      "data-testid": testId,
      children: [topContent, children, hasDrawerFooter && /*#__PURE__*/_jsxs("div", {
        ...{
          className: "x78zum5 xdt5ytf xr1yuqi x1txdalj x1xye8es"
        },
        children: [footer, footerIcons && /*#__PURE__*/_jsx("div", {
          ...{
            className: "x78zum5 x6s0dn4 xzye2dw"
          },
          children: footerIcons
        })]
      })]
    });
  }

  // =========================================================================
  // Drawer-content mode — render just items (no MobileNav wrapper)
  // Used when TopNav owns the drawer and SideNav items are nested inside
  // =========================================================================
  if (renderMode === 'drawer-content') {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [topContent, children, hasDrawerFooter && /*#__PURE__*/_jsxs("div", {
        ...{
          className: "x78zum5 xdt5ytf xr1yuqi x1txdalj x1xye8es"
        },
        children: [footer, footerIcons && /*#__PURE__*/_jsx("div", {
          ...{
            className: "x78zum5 x6s0dn4 xzye2dw"
          },
          children: footerIcons
        })]
      })]
    });
  }

  // =========================================================================
  // Default mode — full sidebar
  // =========================================================================
  const hasStickyTop = !!(header || topContent);
  const hasStickyBottom = !!(footer || footerIcons);
  // The built-in collapse button only renders when collapse is enabled and it
  // hasn't been opted out via `collapsible.hasButton: false` (e.g. when the
  // consumer places a SideNavCollapseButton in the header instead).
  const showCollapseButton = isCollapsible && hasCollapseButton;

  // When resizable, override the nav width via inline style
  const resizableNavStyle = isResizable ? {
    ...(style ?? {}),
    width: collapsed ? undefined : resizableHook.size
  } : style;
  const navElement = /*#__PURE__*/_jsxs("nav", {
    ref: mergeRefs(ref, navRef),
    role: "navigation",
    "aria-label": t('@astryx.sideNav.label'),
    "data-testid": testId,
    ...mergeProps(themeProps('side-nav'), stylex.props(styles.root, collapsed && styles.rootCollapsed, xstyle), className, resizableNavStyle),
    ...props,
    children: [hasStickyTop && /*#__PURE__*/_jsxs("div", {
      ...{
        0: {
          className: "x78zum5 xdt5ytf x2lah0s x7wzq59 x13vifvy x1vjfegm xgcd1z6 x1xye8es x1wesfrj xf314gf x1txdalj"
        },
        1: {
          className: "x78zum5 xdt5ytf x2lah0s x7wzq59 x13vifvy x1vjfegm xgcd1z6 x1xye8es x1wesfrj xf314gf x1txdalj x6s0dn4"
        }
      }[!!collapsed << 0],
      children: [header, topContent && /*#__PURE__*/_jsx("div", {
        ...{},
        children: topContent
      })]
    }), /*#__PURE__*/_jsx("div", {
      ...{
        0: {
          className: "x98rzlu x1odjw0f x6ikm8r xf314gf x1xye8es x1wesfrj"
        },
        4: {
          className: "x98rzlu x1odjw0f x6ikm8r xf314gf x78zum5 xdt5ytf x6s0dn4 x1xye8es x1wesfrj"
        },
        2: {
          className: "x98rzlu x1odjw0f x6ikm8r xf314gf xfsso4q x1wesfrj"
        },
        6: {
          className: "x98rzlu x1odjw0f x6ikm8r xf314gf x78zum5 xdt5ytf x6s0dn4 xfsso4q x1wesfrj"
        },
        1: {
          className: "x98rzlu x1odjw0f x6ikm8r xf314gf x1xye8es xy143xn"
        },
        5: {
          className: "x98rzlu x1odjw0f x6ikm8r xf314gf x78zum5 xdt5ytf x6s0dn4 x1xye8es xy143xn"
        },
        3: {
          className: "x98rzlu x1odjw0f x6ikm8r xf314gf xfsso4q xy143xn"
        },
        7: {
          className: "x98rzlu x1odjw0f x6ikm8r xf314gf x78zum5 xdt5ytf x6s0dn4 xfsso4q xy143xn"
        }
      }[!!collapsed << 2 | !!hasStickyTop << 1 | !!hasStickyBottom << 0],
      children: children
    }), (hasStickyBottom || showCollapseButton) && /*#__PURE__*/_jsxs("div", {
      ...{
        0: {
          className: "x78zum5 xdt5ytf x2lah0s xr1yuqi x7wzq59 x1ey2m1c xgcd1z6 x1txdalj xf314gf xfsso4q x1wesfrj"
        },
        1: {
          className: "x78zum5 xdt5ytf x2lah0s xr1yuqi x7wzq59 x1ey2m1c xgcd1z6 x1txdalj xf314gf x1wesfrj xexx8yu"
        }
      }[!!collapsed << 0],
      children: [footer, /*#__PURE__*/_jsxs("div", {
        ...{
          0: {
            className: "x78zum5 x6s0dn4 xzye2dw"
          },
          1: {
            className: "x78zum5 x6s0dn4 xzye2dw x3ieub6"
          }
        }[!!collapsed << 0],
        children: [showCollapseButton && /*#__PURE__*/_jsx(SideNavCollapseButton, {}), footerIcons]
      })]
    })]
  });

  // Overlay drag handle inside the nav when resizable.
  // Uses ResizeHandle in overlay mode so the handle sits inside
  // the panel's overflow: clip bounds.
  const content = showResizeHandle ? /*#__PURE__*/_jsxs("div", {
    ...{
      className: "x1n2onr6 x78zum5 x2lah0s x5yr21d"
    },
    children: [navElement, /*#__PURE__*/_jsx(ResizeHandle, {
      "data-testid": "astryx-sidenav-resize-handle",
      direction: "horizontal",
      position: "overlay",
      pillPlacement: "end",
      isAlwaysVisible: false,
      resizable: resizableHook.props,
      label: t('@astryx.sideNav.resizeSidebar')
    })]
  }) : navElement;
  if (isCollapsible) {
    return /*#__PURE__*/_jsx(SideNavCollapseContext, {
      value: collapseContext,
      children: content
    });
  }
  return content;
}
SideNav.displayName = 'SideNav';